import React from "react";
import PropTypes from "prop-types";
import {
  setColourShade,
  setColourShadeIfMissing,
  SIGNOL_COLOUR_NAMES,
} from "@signolteam/shared/colours";
import * as icons from "@src/assets/icons";
import AppUtils from "@src/utils/appUtils";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const Icon = ({
  name,
  size = "md",
  textColor = "black",
  textColorShade = 500,
  backgroundColor = "none",
  backgroundColorShade = 500,
  centered = true,
  padding = true,
  border = false,
  clickable = false,
  inline = false,
  className = "",
}) => {
  if (!Object.prototype.hasOwnProperty.call(icons, name)) {
    console.warn("Unknown icon name: ", name);
    return <></>;
  }

  const resolvedTextColor = setColourShadeIfMissing(textColor, textColorShade);
  const resolvedBgColor = setColourShade(backgroundColor, backgroundColorShade);
  const resolvedBgColorHover = setColourShade(backgroundColor, 700);
  const isCustomNumeric = /^\d+$/.test(size) && Number(size) >= 150;

  const cssClasses = `${className} ${
    centered && !inline ? "mx-auto" : ""
  } rounded-full ${
    isCustomNumeric ? "" : `w-${size} h-${size}`
  } bg-${resolvedBgColor} text-${
    textColor === "black" || textColor === "white"
      ? textColor
      : resolvedTextColor
  } ${border ? "border-black border-2" : ""}
      ${
        clickable
          ? `hover:bg-${resolvedBgColorHover} transition duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-101 hover:fontbold`
          : ""
      }
      `;

  const style = isCustomNumeric
    ? { width: `${size}px`, height: `${size}px` }
    : {};

  if (inline) {
    return (
      <span
        className={
          "inline-flex justify-center items-center align-middle " + cssClasses
        }
      >
        {icons[name]()}
      </span>
    );
  }
  return (
    <div
      className={"flex justify-center items-center " + cssClasses}
      style={style}
    >
      <span className={`${padding ? "h-[80%] m-auto" : ""}`}>
        {icons[name]()}
      </span>
    </div>
  );
};

export const iconControls = {
  name: COMMON_CONTROLS.icon_name,
  textColor: COMMON_CONTROLS.colour_name,
  textColorShade: {
    ...COMMON_CONTROLS.colour_shade,
    description: "Text color shade (100-900)",
    defaultValue: 500,
  },
  backgroundColor: {
    ...COMMON_CONTROLS.colour_name_include_transparent,
    description: "Background color",
    defaultValue: "none",
  },
  backgroundColorShade: {
    propTypes: PropTypes.number,
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES.reduce(
      (acc, colour) => [...acc, 100, 200, 300, 400, 500, 600, 700, 800, 900],
      [],
    ),
    description: "Background color shade (100-900)",
    defaultValue: 500,
    if: { arg: "backgroundColor", neq: "none" },
  },
  border: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Border around the icon",
  },
  centered: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Center the icon horizontally and vertically",
  },
  clickable: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Add hover/click styling",
  },
  inline: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Inline display layout",
  },
  padding: {
    propTypes: PropTypes.bool,
    controlType: "boolean",
    description: "Padding around the icon",
  },
  size: {
    propTypes: PropTypes.string,
    description: "Icon size (e.g., 'sm', 'md', 'lg', or a custom size)",
    controlType: "select",
    options: ["sm", "md", "lg", "xl", "10", "14", "20", "180"],
  },
  className: COMMON_CONTROLS.className,
};

Icon.propTypes = AppUtils.getPropTypeFromControls(iconControls);

export default Icon;
